import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Avatar,
  AvatarGroup,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Skeleton,
  LinearProgress,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  X,
  Play,
  Square,
  Clock,
  User,
  Users,
  Eye,
  MessageSquare,
  Paperclip,
  Calendar,
  FolderKanban,
  Edit2,
  Trash2,
  Plus,
  CheckCircle,
  AlertCircle,
  Edit,
  GitBranch,
  Link2,
  FileText,
  Bug,
  GitCommit,
  CalendarClock,
  MapPin,
  Target,
  ArrowLeft,
  CheckSquare,
  Repeat,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync, stopTimerAsync, clearTimerForTask } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isAdminOrManager, normalizeRole } from '@/shared/utils/roles';
import { Task, TaskStatus, TaskPriority, TimeLog, TaskComment, RACIRole, LinkedDocument, LinkedIssue, LinkedCommit, IssueStatus, DueDateChange, Milestone } from '@/types';
import { taskService, timeService, projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useFeaturePermission } from '@/shared/contexts/PermissionsContext';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';
import TaskSubtaskTree from './TaskSubtaskTree';
import TaskDependencyViewer from './TaskDependencyViewer';
import TaskDependencyMapper from './TaskDependencyMapper';
import CreateSubtaskModal from './CreateSubtaskModal';
import TaskDocumentsTab from './TaskDocumentsTab';
import TaskIssuesTab from './TaskIssuesTab';
import TaskCommitsTab from './TaskCommitsTab';
import TaskChecklistTab from './TaskChecklistTab';
import TaskRecurrenceTab from './TaskRecurrenceTab';
import DueDateUpdateModal, { DueDateSummary } from './DueDateUpdateModal';
import DependencyBlockedWarningModal, { validateTaskCompletion, validateSubtasksCompletion } from './DependencyBlockedWarningModal';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { useTodaysPickedTaskIds } from '@/hooks/useTodaysPickedTaskIds';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { PICK_BEFORE_TIMER_MESSAGE } from '@/shared/constants/timerRules';

dayjs.extend(relativeTime);

interface TaskDetailModalProps {
  open: boolean;
  taskId: string | null;
  onClose: () => void;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

/**
 * TaskDetailModal
 * Comprehensive task detail view with RACI, time logs, comments
 * Mobile-first responsive design
 * Follows enterprise task management patterns
 */
const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  taskId,
  onClose,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);
  const todaysPickedTaskIds = useTodaysPickedTaskIds();
  const { showError } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [comment, setComment] = useState('');
  const [manualLogForm, setManualLogForm] = useState({
    duration: '',
    notes: '',
    showForm: false,
  });
  // State for subtask and dependency modals
  const [showCreateSubtaskModal, setShowCreateSubtaskModal] = useState(false);
  const [showDependencyMapper, setShowDependencyMapper] = useState(false);
  const [subtaskParentTask, setSubtaskParentTask] = useState<Task | null>(null);
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [showDependencyWarning, setShowDependencyWarning] = useState(false);
  const [blockingTasksForWarning, setBlockingTasksForWarning] = useState<Task[]>([]);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    confirmText: string;
    type: 'warning' | 'danger' | 'info' | 'success';
    onConfirm: (reason?: string) => void | Promise<void>;
    reasonInput?: {
      label?: string;
      placeholder?: string;
      required?: boolean;
    };
  } | null>(null);

  // Fetch task details
  const { data: taskData, isLoading } = useQuery({
    queryKey: QUERY_KEYS.task(taskId!),
    queryFn: () => taskService.getTask(taskId!),
    enabled: !!taskId && open,
  });

  const task = taskData ?? undefined;
  
  // Get feature permissions (gates RACI checks)
  const hasEditPermission = useFeaturePermission('tasks', 'task_management', 'edit');
  const hasDeletePermission = useFeaturePermission('tasks', 'task_management', 'delete');
  
  // RACI role checks
  const effectiveRole = normalizeRole(user?.role);
  const userId = user?.id;
  const isCreator = !!task && !!userId && task.createdBy === userId;
  const isResponsible = !!task && !!userId && (task.raci?.responsible || []).includes(userId);
  const isAccountable = !!task && !!userId && task.raci?.accountable === userId;
  const isConsulted = !!task && !!userId && (task.raci?.consulted || []).includes(userId);
  const isInformed = !!task && !!userId && (task.raci?.informed || []).includes(userId);
  const isAssigned = isResponsible || isAccountable || isConsulted || isInformed;
  
  // Combined permissions: feature permission gates RACI checks
  const canEditTask = hasEditPermission &&
    (isAdminOrManager(user?.role) || (effectiveRole === 'engineer' && (isCreator || isResponsible || isAccountable)))
    && task?.status !== TaskStatus.COMPLETED
    && task?.status !== TaskStatus.CANCELLED;
  // Status: R, A, or Creator can change status. Only A can complete/cancel.
  const canUpdateStatus = hasEditPermission && (isAdminOrManager(user?.role) || isCreator || isResponsible || isAccountable);
  // Complete/Cancel: only Accountable (admin/manager always can). Creator alone is NOT enough.
  const canCompleteTask = hasEditPermission && (isAdminOrManager(user?.role) || isAccountable);
  // Delete: feature permission required plus RACI role
  const canDeleteTask = hasDeletePermission
    && task?.status !== TaskStatus.COMPLETED
    && (isAdminOrManager(user?.role) || isCreator || isAccountable);
  // Comments: R, A, C, or Creator (Informed cannot comment)
  const canComment = isAdminOrManager(user?.role) || isCreator || isResponsible || isAccountable || isConsulted;
  // Subtasks/attachments: R, A, Creator only
  const canAddSubtasks = hasEditPermission && (isAdminOrManager(user?.role) || isCreator || isResponsible || isAccountable);
  const canAddAttachments = hasEditPermission && (isAdminOrManager(user?.role) || isCreator || isResponsible || isAccountable || isConsulted);
  // Timer: ONLY Responsible users can use timer
  const canUseTimer = isResponsible;
  const isTaskCompleted = task?.status === TaskStatus.COMPLETED;
  const canEditTabContent = canEditTask && !isTaskCompleted;

  // Fetch all tasks for subtasks and dependencies
  const { data: allTasksData } = useQuery({
    queryKey: QUERY_KEYS.tasks,
    queryFn: () =>
      taskService.getTasks(task?.projectId ? { projectId: task.projectId } : undefined, 1, 500),
    enabled: !!taskId && open,
  });

  const allTasks = allTasksData?.items ?? [];

  // Fetch project data for milestone name lookup
  const { data: projectData } = useQuery({
    queryKey: QUERY_KEYS.project(task?.projectId || ''),
    queryFn: () => projectService.getProject(task?.projectId || ''),
    enabled: !!task?.projectId && open,
  });

  const project = projectData ?? undefined;

  // Get milestone name from project
  const getMilestoneName = (): { name: string; isCompleted: boolean; dueDate?: string } | null => {
    if (!task?.milestoneId || !project?.milestones) return null;
    const milestone = project.milestones.find((m: Milestone) => m.id === task.milestoneId);
    if (!milestone) return null;
    return { 
      name: milestone.name, 
      isCompleted: milestone.isCompleted,
      dueDate: milestone.dueDate 
    };
  };

  const milestoneInfo = getMilestoneName();

  // Fetch time logs
  const { data: timeLogsData } = useQuery({
    queryKey: QUERY_KEYS.taskTimeLogs(taskId!),
    queryFn: () => timeService.getTaskTimeLogs(taskId!),
    enabled: !!taskId && open,
  });

  const timeLogs = timeLogsData ?? [];

  // Fetch comments
  const { data: commentsData } = useQuery({
    queryKey: ['tasks', taskId!, 'comments'],
    queryFn: () => taskService.getComments(taskId!),
    enabled: !!taskId && open,
  });

  const taskComments = commentsData ?? [];

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      taskService.updateTaskStatus(taskId, status),
    onSuccess: (_data, { taskId: updatedTaskId, status }) => {
      if (status === TaskStatus.COMPLETED) {
        dispatch(clearTimerForTask(updatedTaskId));
        onClose();
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
    },
  });

  // Comment mutation
  const commentMutation = useMutation({
    mutationFn: (content: string) =>
      taskService.addComment(taskId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId!, 'comments'] });
      setComment('');
    },
  });

  // Manual time log mutation
  const manualLogMutation = useMutation({
    mutationFn: ({ duration, notes }: { duration: number; notes?: string }) =>
      timeService.addManualEntry(taskId!, duration, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskTimeLogs(taskId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
      setManualLogForm({ duration: '', notes: '', showForm: false });
    },
  });

  const isTimerActive = activeTimers.some(t => t.taskId === taskId);
  const isPickedForToday = taskId ? todaysPickedTaskIds.has(taskId) : false;

  const handleTimerToggle = () => {
    if (!user || !taskId || !task) return;
    if (isTimerActive) {
      setConfirmAction({
        title: 'Stop timer',
        message: 'Stop the timer and log the time.',
        confirmText: 'Stop',
        type: 'warning',
        reasonInput: {
          label: 'Reason',
          placeholder: 'Why are you stopping this timer?',
          required: true,
        },
        onConfirm: (reason) => { dispatch(stopTimerAsync({ taskId, reason })); },
      });
    } else if (!isPickedForToday) {
      showError(PICK_BEFORE_TIMER_MESSAGE);
    } else {
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle: task.title }));
    }
  };

  const handleStatusChange = (status: TaskStatus) => {
    if (!taskId || !task) return;
    
    // If trying to complete, validate dependencies first
    if (status === TaskStatus.COMPLETED) {
      // Check blocking dependencies
      const validation = validateTaskCompletion(task, allTasks);
      if (!validation.canComplete) {
        // Get the incomplete blocking tasks
        const incompleteBlockers = validation.blockingTasks.filter(
          (t) => t.status !== TaskStatus.COMPLETED && t.status !== TaskStatus.CANCELLED
        );
        setBlockingTasksForWarning(validation.blockingTasks);
        setShowDependencyWarning(true);
        return;
      }
      
      // Check incomplete subtasks
      const incompleteSubtasks = validateSubtasksCompletion(task, allTasks);
      if (incompleteSubtasks.length > 0) {
        setBlockingTasksForWarning(incompleteSubtasks);
        setShowDependencyWarning(true);
        return;
      }
      setConfirmAction({
        title: 'Mark as completed',
        message: 'Mark this task as completed?',
        confirmText: 'Mark completed',
        type: 'success',
        onConfirm: async () => {
          // Stop timer first if running
          const runningTimer = activeTimers.find(t => t.taskId === taskId);
          if (runningTimer) {
            await dispatch(stopTimerAsync({ taskId })).unwrap();
          }
          statusMutation.mutate({ taskId, status });
        },
      });
      return;
    }

    statusMutation.mutate({ taskId, status });
  };

  const handleAddComment = () => {
    if (isTaskCompleted || !canComment) return;
    if (comment.trim()) {
      commentMutation.mutate(comment.trim());
    }
  };

  const handleAddManualLog = () => {
    if (isTaskCompleted) return;
    const duration = parseInt(manualLogForm.duration);
    if (duration && duration > 0) {
      manualLogMutation.mutate({
        duration,
        notes: manualLogForm.notes || undefined,
      });
    }
  };

  // Subtask handlers
  const handleAddSubtask = (parentTask: Task) => {
    setSubtaskParentTask(parentTask);
    setShowCreateSubtaskModal(true);
  };

  const handleSubtaskClick = (subtask: Task) => {
    onClose();
    window.history.pushState({}, '', `/tasks?taskId=${subtask.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // Dependency handlers
  const handleRemoveDependency = async (fromTaskId: string, toTaskId: string) => {
    try {
      const fromTask = allTasks.find((t) => t.id === fromTaskId);
      const toTask = allTasks.find((t) => t.id === toTaskId);
      if (!fromTask || !toTask) return;

      // Update the blocking task (remove toTaskId from blockingTaskIds)
      await taskService.updateTask(fromTaskId, {
        blockingTaskIds: (fromTask.blockingTaskIds || []).filter((id) => id !== toTaskId),
      });

      // Update the blocked task (remove fromTaskId from blockedByTaskIds)
      await taskService.updateTask(toTaskId, {
        blockedByTaskIds: (toTask.blockedByTaskIds || []).filter((id) => id !== fromTaskId),
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
    } catch (error) {
      console.error('Failed to remove dependency:', error);
    }
  };

  // Documents handlers
  const handleAddDocument = async (document: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => {
    if (!task || !user) return;
    const newDocument: LinkedDocument = {
      ...document,
      id: `doc-${Date.now()}`,
      linkedAt: new Date().toISOString(),
      linkedBy: user.id,
    };
    const updatedDocuments = [...(task.linkedDocuments || []), newDocument];
    await taskService.updateTask(task.id, { linkedDocuments: updatedDocuments });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!task) return;
    const updatedDocuments = (task.linkedDocuments || []).filter(d => d.id !== documentId);
    await taskService.updateTask(task.id, { linkedDocuments: updatedDocuments });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  // Issues handlers (backend linked_issues API)
  const handleAddIssue = async (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => {
    if (!task || !user) return;
    await taskService.addLinkedIssue(task.id, issue);
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  const handleRemoveIssue = async (issueId: string) => {
    if (!task) return;
    await taskService.deleteLinkedIssue(task.id, issueId);
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  const handleUpdateIssueStatus = async (issueId: string, status: IssueStatus) => {
    if (!task || !user) return;
    await taskService.updateLinkedIssue(task.id, issueId, {
      status,
      resolvedAt: status === 'resolved' ? new Date().toISOString() : undefined,
      resolvedBy: status === 'resolved' ? user.id : undefined,
    });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  const handleCreateIssueSubtask = async (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => {
    if (!task || !user) return;
    const subtaskPayload = {
      title: `[${issue.type.toUpperCase()}] ${issue.title}`,
      description: issue.description || '',
      priority: issue.priority === 'critical' ? TaskPriority.P1 : issue.priority === 'high' ? TaskPriority.P2 : issue.priority === 'medium' ? TaskPriority.P3 : TaskPriority.P4,
      parentTaskId: task.id,
      projectId: task.projectId,
      dueDate: task.dueDate,
      raci: { responsible: task.raci.responsible, accountable: task.raci.accountable, consulted: [] as string[], informed: [] as string[] },
    };
    const createdSubtask = await taskService.createSubtask(task.id, subtaskPayload);
    const subtaskId = createdSubtask.id;
    await taskService.addLinkedIssue(task.id, { ...issue, subtaskId });
    await taskService.updateTask(task.id, {
      subtaskIds: [...(task.subtaskIds || []), subtaskId],
      blockedByTaskIds: [...(task.blockedByTaskIds || []), subtaskId],
    });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  // Commits handlers
  const handleAddCommit = async (commit: Omit<LinkedCommit, 'id' | 'linkedAt' | 'linkedBy'>) => {
    if (!task || !user) return;
    const newCommit: LinkedCommit = {
      ...commit,
      id: `commit-${Date.now()}`,
      linkedAt: new Date().toISOString(),
      linkedBy: user.id,
    };
    const updatedCommits = [...(task.linkedCommits || []), newCommit];
    await taskService.updateTask(task.id, { linkedCommits: updatedCommits });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  const handleRemoveCommit = async (commitId: string) => {
    if (!task) return;
    const updatedCommits = (task.linkedCommits || []).filter(c => c.id !== commitId);
    await taskService.updateTask(task.id, { linkedCommits: updatedCommits });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
  };

  // Due Date Update handler
  const handleDueDateUpdate = async (newDueDate: string, reason: string) => {
    if (!task || !user) return;

    const originalDueDate = task.originalDueDate || task.dueDate;
    const delayDays = dayjs(newDueDate).diff(dayjs(task.dueDate), 'day');
    const totalDelayDays = dayjs(newDueDate).diff(dayjs(originalDueDate), 'day');

    const newHistoryEntry: DueDateChange = {
      id: `ddc-${Date.now()}`,
      previousDueDate: task.dueDate,
      newDueDate,
      reason,
      changedAt: new Date().toISOString(),
      changedBy: user.id,
      delayDays,
    };

    // Track the date from which task was rescheduled
    // If the previous due date was today, add today to rescheduledFromDates
    const previousDueDate = dayjs(task.dueDate).format('YYYY-MM-DD');
    const todayStr = dayjs().format('YYYY-MM-DD');
    const rescheduledFromDates = [...(task.rescheduledFromDates || [])];
    
    // If the task was due today and is being rescheduled to a different day, track it
    if (previousDueDate === todayStr && dayjs(newDueDate).format('YYYY-MM-DD') !== todayStr) {
      if (!rescheduledFromDates.includes(todayStr)) {
        rescheduledFromDates.push(todayStr);
      }
    }

    await taskService.updateTask(task.id, {
      dueDate: newDueDate,
      originalDueDate: task.originalDueDate || task.dueDate, // Set original only if not already set
      dueDateHistory: [...(task.dueDateHistory || []), newHistoryEntry],
      totalDelayDays,
      rescheduledFromDates,
    });

    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overdueTasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.upcomingTasks });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getLiveActualMinutes = () => {
    if (!task) return 0;
    // Sum ALL task time logs from all users, not just current user's actualMinutes
    const totalLoggedMinutes = timeLogs.reduce((sum: number, log: TimeLog) => {
      return sum + (log.duration || 0);
    }, 0);
    // Add currently running timer if any
    const timer = activeTimers.find(t => t.taskId === task.id);
    const runningMinutes = timer ? Math.floor(timer.accumulatedTime / 60) : 0;
    return totalLoggedMinutes + runningMinutes;
  };

  const calculateProgress = (actualMinutes: number) => {
    if (!task) return 0;
    if (task.status === TaskStatus.COMPLETED) return 100;
    if (!task.estimatedMinutes || task.estimatedMinutes === 0) return 0;
    const estimatedMinutes = task.estimatedMinutes || 0;
    return Math.min((actualMinutes / estimatedMinutes) * 100, 100);
  };

  // Get deadline status
  const getDeadlineStatus = () => {
    if (!task) return { color: 'text.secondary', label: '' };
    const now = dayjs();
    const deadline = dayjs(task.dueDate);
    
    if (task.status === TaskStatus.COMPLETED) {
      return { color: 'success.main', label: 'Completed' };
    }
    
    const hoursUntil = deadline.diff(now, 'hour');
    if (hoursUntil < 0) {
      return { color: 'error.main', label: `Overdue by ${Math.abs(hoursUntil)}h` };
    }
    if (hoursUntil < 24) {
      return { color: 'warning.main', label: `Due in ${hoursUntil}h` };
    }
    return { color: 'text.secondary', label: deadline.fromNow() };
  };

  const deadlineStatus = getDeadlineStatus();

  if (!open) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          borderRadius: isMobile ? 0 : 2, 
          maxHeight: isMobile ? '100vh' : '90vh',
        },
      }}
    >
      {isLoading ? (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rounded" height={200} sx={{ mt: 2 }} />
        </Box>
      ) : task ? (
        <>
          {/* Header */}
          <DialogTitle sx={{ pb: 1, px: isMobile ? 2 : 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ flex: 1, pr: 2, display: 'flex', alignItems: isMobile ? 'flex-start' : 'flex-start', gap: isMobile ? 1 : 0, flexDirection: isMobile ? 'row' : 'column' }}>
                {isMobile && (
                  <IconButton onClick={onClose} edge="start" sx={{ mt: -0.5, ml: -1 }}>
                    <ArrowLeft size={20} />
                  </IconButton>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.3 }}>
                    {task.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <PriorityBadge priority={task.priority} />
                    <StatusBadge status={task.status} />
                    <Chip
                      icon={<Clock size={14} />}
                      label={deadlineStatus.label}
                      size="small"
                      sx={{
                        bgcolor: `${deadlineStatus.color}10`,
                        color: deadlineStatus.color,
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                    {task.isOnsite && (
                      <Chip
                        icon={<MapPin size={14} />}
                        label={task.onsiteLocation || 'Onsite'}
                        size="small"
                        sx={{
                          bgcolor: 'info.light',
                          color: 'info.dark',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              {!isMobile && (
                <IconButton onClick={onClose} size="small">
                  <X size={20} />
                </IconButton>
              )}
            </Box>
          </DialogTitle>

          <Divider />

          {/* RACI Role Banner */}
          {task && effectiveRole === 'engineer' && !isAdminOrManager(user?.role) && (
            <Box sx={{ px: isMobile ? 2 : 3, pt: 1.5 }}>
              {isInformed && !isConsulted && !isResponsible && !isAccountable && !isCreator && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 1 }}>
                  <Eye size={16} style={{ color: '#0288d1' }} />
                  <Typography variant="body2" color="info.dark">
                    You are <strong>Informed</strong> on this task — read-only access
                  </Typography>
                </Box>
              )}
              {isConsulted && !isResponsible && !isAccountable && !isCreator && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1 }}>
                  <MessageSquare size={16} style={{ color: '#ed6c02' }} />
                  <Typography variant="body2" color="warning.dark">
                    You are <strong>Consulted</strong> on this task — you can comment &amp; attach files
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <DialogContent sx={{ pt: 0, px: isMobile ? 2 : 3 }}>
            {/* Quick Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant={isTimerActive ? 'contained' : 'outlined'}
                color={isTimerActive ? 'error' : 'primary'}
                startIcon={isTimerActive ? <Square size={16} /> : <Play size={16} />}
                onClick={handleTimerToggle}
                disabled={
                  task.status === TaskStatus.COMPLETED
                  || !canUseTimer
                  || (!isTimerActive && !isPickedForToday)
                }
                title={!isTimerActive && !isPickedForToday ? PICK_BEFORE_TIMER_MESSAGE : undefined}
                size={isMobile ? 'small' : 'medium'}
              >
                {isTimerActive ? 'Stop' : 'Start'}
              </Button>
              {task.status !== TaskStatus.COMPLETED && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircle size={16} />}
                  onClick={() => handleStatusChange(TaskStatus.COMPLETED)}
                  disabled={!canCompleteTask}
                >
                  Mark Complete
                </Button>
              )}
              {onEdit && canEditTask && (
                <IconButton 
                  onClick={() => onEdit(task)}
                  title="Edit task"
                >
                  <Edit2 size={18} />
                </IconButton>
              )}
              {onDelete && canDeleteTask && (
                <IconButton
                  onClick={() =>
                    setConfirmAction({
                      title: 'Delete task',
                      message: 'Permanently delete this task? This cannot be undone.',
                      confirmText: 'Delete',
                      type: 'danger',
                      onConfirm: () => {
                        onDelete(task.id);
                        onClose();
                      },
                    })
                  }
                  color="error"
                >
                  <Trash2 size={18} />
                </IconButton>
              )}
            </Box>

            {/* Time Progress */}
            <Box sx={{ py: 2 }}>
              {(() => {
                const liveActualMinutes = getLiveActualMinutes();
                const estimatedMinutes = task.estimatedMinutes || 0;
                const progress = calculateProgress(liveActualMinutes);
                return (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Time Progress (All Users)
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Total task time logged
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDuration(liveActualMinutes)} / {formatDuration(estimatedMinutes)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: progress > 100 ? 'error.main' : 'primary.main',
                        },
                      }}
                    />
                  </>
                );
              })()}
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                minHeight: isMobile ? 40 : 48,
                '& .MuiTab-root': {
                  minHeight: isMobile ? 40 : 48,
                  minWidth: isMobile ? 'auto' : 'auto',
                  px: isMobile ? 1.5 : 2,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={isMobile ? '' : 'Details'} icon={<Eye size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Subtasks'} icon={<GitBranch size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Deps'} icon={<Link2 size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Docs'} icon={<FileText size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Issues'} icon={<Bug size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Commits'} icon={<GitCommit size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'RACI'} icon={<Users size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Time'} icon={<Clock size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Comments'} icon={<MessageSquare size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Checklist'} icon={<CheckSquare size={isMobile ? 14 : 16} />} iconPosition="start" />
              <Tab label={isMobile ? '' : 'Recurring'} icon={<Repeat size={isMobile ? 14 : 16} />} iconPosition="start" />
            </Tabs>

            {/* Details Tab */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Due Date Summary - Show if overdue or has delay */}
                {(dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== TaskStatus.COMPLETED) || (task.totalDelayDays && task.totalDelayDays > 0) ? (
                  <DueDateSummary task={task} onUpdateClick={() => setShowDueDateModal(true)} />
                ) : null}

                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {task.description || 'No description provided'}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Project
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <FolderKanban size={16} style={{ color: '#64748b' }} />
                      <Typography variant="body2">{project?.name || task.projectName || task.projectId || 'No project'}</Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Milestone
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Target size={16} style={{ color: milestoneInfo?.isCompleted ? '#16a34a' : '#f59e0b' }} />
                      {milestoneInfo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{milestoneInfo.name}</Typography>
                          {milestoneInfo.isCompleted && (
                            <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                          {milestoneInfo.dueDate && !milestoneInfo.isCompleted && (
                            <Typography variant="caption" color="text.secondary">
                              (Due: {dayjs(milestoneInfo.dueDate).format('MMM D')})
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No milestone</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Start Date
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Calendar size={16} style={{ color: '#64748b' }} />
                      <Typography variant="body2">
                        {task.startDate && dayjs(task.startDate).isValid() ? dayjs(task.startDate).format('MMM D, YYYY') : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Due Date
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Calendar size={16} style={{ color: '#64748b' }} />
                      <Typography variant="body2" sx={{ color: deadlineStatus.color }}>
                        {task.dueDate && dayjs(task.dueDate).isValid() ? dayjs(task.dueDate).format('MMM D, YYYY') : '—'}
                      </Typography>
                      {task.status !== TaskStatus.COMPLETED && (
                        <Tooltip title="Update due date">
                          <IconButton size="small" onClick={() => setShowDueDateModal(true)}>
                            <CalendarClock size={14} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {task.originalDueDate && task.originalDueDate !== task.dueDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Originally: {dayjs(task.originalDueDate).format('MMM D, YYYY')}
                        {task.totalDelayDays && task.totalDelayDays > 0 && (
                          <Chip
                            label={`${task.totalDelayDays}d delayed`}
                            size="small"
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#fee2e2', color: '#dc2626' }}
                          />
                        )}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Estimated Time
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Clock size={16} style={{ color: '#64748b' }} />
                      <Typography variant="body2">
                        {task.estimatedMinutes ? `${Math.floor(task.estimatedMinutes / 60)}h ${task.estimatedMinutes % 60}m (${task.estimatedMinutes} min)` : 'Not set'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {dayjs(task.createdAt).format('MMM D, YYYY')}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Updated
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {dayjs(task.updatedAt).fromNow()}
                    </Typography>
                  </Box>
                </Box>

                {task.attachments && task.attachments.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        Attachments ({task.attachments.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        {task.attachments.map((attachment: any) => (
                          <Chip
                            key={attachment.id}
                            icon={<Paperclip size={14} />}
                            label={attachment.name}
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(attachment.url, '_blank')}
                          />
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </TabPanel>

            {/* Subtasks Tab */}
            <TabPanel value={activeTab} index={1}>
              <TaskSubtaskTree
                parentTask={task}
                allTasks={allTasks}
                onTaskClick={handleSubtaskClick}
                onAddSubtask={(canAddSubtasks && !isTaskCompleted) ? handleAddSubtask : undefined}
                isLoading={!allTasksData}
              />
            </TabPanel>

            {/* Dependencies Tab */}
            <TabPanel value={activeTab} index={2}>
              <TaskDependencyViewer
                task={task}
                allTasks={allTasks}
                onTaskClick={handleSubtaskClick}
                onRemoveDependency={canEditTabContent ? handleRemoveDependency : undefined}
                onAddDependency={canEditTabContent ? () => setShowDependencyMapper(true) : undefined}
                readonly={!canEditTabContent}
                isLoading={!allTasksData}
              />
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={activeTab} index={3}>
              <TaskDocumentsTab
                task={task}
                onAddDocument={(canAddAttachments && !isTaskCompleted) ? handleAddDocument : undefined}
                onRemoveDocument={(canAddSubtasks && !isTaskCompleted) ? handleRemoveDocument : undefined}
                canEdit={(canAddAttachments && !isTaskCompleted)}
                isLoading={!taskData}
              />
            </TabPanel>

            {/* Issues Tab */}
            <TabPanel value={activeTab} index={4}>
              <TaskIssuesTab
                task={task}
                allTasks={allTasks}
                onAddIssue={canEditTabContent ? handleAddIssue : undefined}
                onRemoveIssue={canEditTabContent ? handleRemoveIssue : undefined}
                onUpdateIssueStatus={canEditTabContent ? handleUpdateIssueStatus : undefined}
                onCreateIssueSubtask={canEditTabContent ? handleCreateIssueSubtask : undefined}
                canEdit={canEditTabContent}
                isLoading={!taskData}
              />
            </TabPanel>

            {/* Commits Tab */}
            <TabPanel value={activeTab} index={5}>
              <TaskCommitsTab
                task={task}
                onAddCommit={canEditTabContent ? handleAddCommit : undefined}
                onRemoveCommit={canEditTabContent ? handleRemoveCommit : undefined}
                canEdit={canEditTabContent}
                isLoading={!taskData}
              />
            </TabPanel>

            {/* RACI Tab */}
            <TabPanel value={activeTab} index={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Chip label={`R ${task.raci.responsible.length}`} color="primary" size="small" />
                  <Chip label={`A ${task.raci.accountable ? 1 : 0}`} color="warning" size="small" />
                  <Chip label={`C ${task.raci.consulted.length}`} color="info" size="small" />
                  <Chip label={`I ${task.raci.informed.length}`} color="secondary" size="small" />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  {[
                    {
                      key: 'responsible',
                      label: 'Responsible',
                      short: 'R',
                      description: 'People who do the work',
                      color: 'primary.main',
                      users: task.raci.responsible,
                      names: task.raciNames?.responsible,
                    },
                    {
                      key: 'accountable',
                      label: 'Accountable',
                      short: 'A',
                      description: 'Owner of the final outcome',
                      color: 'warning.main',
                      users: task.raci.accountable ? [task.raci.accountable] : [],
                      names: task.raciNames?.accountable ? [task.raciNames.accountable] : [],
                    },
                    {
                      key: 'consulted',
                      label: 'Consulted',
                      short: 'C',
                      description: 'Provide input and feedback',
                      color: 'info.main',
                      users: task.raci.consulted,
                      names: task.raciNames?.consulted,
                    },
                    {
                      key: 'informed',
                      label: 'Informed',
                      short: 'I',
                      description: 'Kept in the loop on progress',
                      color: 'secondary.main',
                      users: task.raci.informed,
                      names: task.raciNames?.informed,
                    },
                  ].map((section) => (
                    <Box
                      key={section.key}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: section.color,
                            color: 'common.white',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {section.short}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {section.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {section.description}
                          </Typography>
                        </Box>
                        <Chip label={section.users.length} size="small" />
                      </Box>

                      {section.users.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {section.users.map((userId, idx) => {
                            const displayName = section.names?.[idx] || userId;
                            return (
                              <Chip
                                key={`${section.key}-${userId}`}
                                avatar={<Avatar sx={{ bgcolor: section.color }}>{displayName.charAt(0).toUpperCase()}</Avatar>}
                                label={displayName}
                                variant="outlined"
                                sx={{ borderColor: 'divider' }}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None assigned
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </TabPanel>

            {/* Time Logs Tab */}
            <TabPanel value={activeTab} index={7}>
              {/* Manual Time Entry Form */}
              {!manualLogForm.showForm ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={() => {
                    if (isTaskCompleted) return;
                    setManualLogForm({ ...manualLogForm, showForm: true });
                  }}
                  disabled={isTaskCompleted}
                  sx={{ mb: 2 }}
                >
                  Add Manual Entry
                </Button>
              ) : (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Add Manual Time Entry
                  </Typography>
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      label="Duration (minutes)"
                      type="number"
                      value={manualLogForm.duration}
                      disabled={isTaskCompleted}
                      onChange={(e) =>
                        setManualLogForm({ ...manualLogForm, duration: e.target.value })
                      }
                      placeholder="e.g., 60"
                    />
                    <TextField
                      size="small"
                      label="Notes (optional)"
                      multiline
                      rows={2}
                      value={manualLogForm.notes}
                      disabled={isTaskCompleted}
                      onChange={(e) =>
                        setManualLogForm({ ...manualLogForm, notes: e.target.value })
                      }
                      placeholder="What did you work on?"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddManualLog}
                        disabled={isTaskCompleted || !manualLogForm.duration || manualLogMutation.isPending}
                      >
                        Add Entry
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setManualLogForm({ duration: '', notes: '', showForm: false })
                        }
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Total Time Summary */}
              {timeLogs.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: 'primary.50',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Total Time Logged
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatDuration(
                      timeLogs.reduce((sum: number, log: TimeLog) => sum + (log.duration || 0), 0)
                    )}
                  </Typography>
                </Box>
              )}

              <List disablePadding>
                {timeLogs.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Clock size={40} style={{ color: '#94a3b8', marginBottom: 8 }} />
                    <Typography color="text.secondary">
                      No time logged yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Start the timer or add a manual entry
                    </Typography>
                  </Box>
                ) : (
                  timeLogs.map((log: TimeLog) => (
                    <ListItem
                      key={log.id}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: log.isManual ? 'warning.100' : 'primary.100',
                          }}
                        >
                          {log.isManual ? (
                            <Edit size={20} style={{ color: '#f59e0b' }} />
                          ) : (
                            <Clock size={20} style={{ color: '#2563eb' }} />
                          )}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 0.5,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatDuration(log.duration)}
                              </Typography>
                              {log.isManual && (
                                <Chip
                                  label="Manual"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: 'warning.50',
                                    color: 'warning.dark',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(log.startTime).format('MMM D, h:mm A')}
                              {log.endTime && ` - ${dayjs(log.endTime).format('MMM D, h:mm A')}`}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Logged by {log.userName && log.userName.trim() ? log.userName : (log.userId || 'Unknown user')}
                            </Typography>
                            {log.notes ? (
                              <Typography variant="body2" color="text.secondary">
                                {log.notes}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                No notes
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </TabPanel>

            {/* Comments Tab */}
            <TabPanel value={activeTab} index={8}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={!canComment ? 'Informed members cannot comment' : 'Add a comment...'}
                  value={comment}
                  disabled={isTaskCompleted || !canComment}
                  onChange={(e) => setComment(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={handleAddComment}
                  disabled={isTaskCompleted || !canComment || !comment.trim() || commentMutation.isPending}
                >
                  Add Comment
                </Button>
                {isTaskCompleted && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Comments are disabled after task completion.
                  </Typography>
                )}
                {!canComment && !isTaskCompleted && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Informed members have read-only access.
                  </Typography>
                )}
              </Box>

              <List disablePadding>
                {taskComments && taskComments.length > 0 ? (
                  taskComments.map((c: TaskComment) => (
                    <ListItem
                      key={c.id}
                      sx={{
                        px: 0,
                        alignItems: 'flex-start',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {(c.createdByName || c.createdBy)?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="subtitle2">{c.createdByName || c.createdBy || 'User'}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(c.createdAt).fromNow()}
                            </Typography>
                          </Box>
                        }
                        secondary={c.content}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <MessageSquare size={40} style={{ color: '#94a3b8', marginBottom: 8 }} />
                    <Typography color="text.secondary">
                      No comments yet
                    </Typography>
                  </Box>
                )}
              </List>
            </TabPanel>

            {/* Checklist Tab */}
            <TabPanel value={activeTab} index={9}>
              <TaskChecklistTab taskId={task.id} canEdit={canEditTabContent} />
            </TabPanel>

            {/* Recurring Tab */}
            <TabPanel value={activeTab} index={10}>
              <TaskRecurrenceTab taskId={task.id} canEdit={canEditTabContent} />
            </TabPanel>
          </DialogContent>

          {!isMobile && (
            <>
              <Divider />
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Close</Button>
              </DialogActions>
            </>
          )}
        </>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: 16 }} />
          <Typography variant="h6" color="error">
            Task not found
          </Typography>
        </Box>
      )}
    </Dialog>

      {/* Create Subtask Modal */}
      {subtaskParentTask && (
        <CreateSubtaskModal
          open={showCreateSubtaskModal}
          onClose={() => {
            setShowCreateSubtaskModal(false);
            setSubtaskParentTask(null);
          }}
          parentTask={subtaskParentTask}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
          }}
        />
      )}

      {/* Dependency Mapper Modal */}
      {task && (
        <TaskDependencyMapper
          open={showDependencyMapper}
          onClose={() => setShowDependencyMapper(false)}
          task={task}
          allTasks={allTasks}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId!) });
          }}
        />
      )}

      {/* Due Date Update Modal */}
      {task && (
        <DueDateUpdateModal
          open={showDueDateModal}
          task={task}
          onClose={() => setShowDueDateModal(false)}
          onUpdate={handleDueDateUpdate}
        />
      )}

      {/* Dependency Blocked Warning Modal */}
      {task && (
        <DependencyBlockedWarningModal
          open={showDependencyWarning}
          onClose={() => setShowDependencyWarning(false)}
          task={task}
          blockingTasks={blockingTasksForWarning}
          onViewTask={(viewTaskId) => {
            setShowDependencyWarning(false);
            onClose();
            // Parent should handle navigation to the blocking task
            window.history.pushState({}, '', `/tasks?taskId=${viewTaskId}`);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          onForceComplete={
            canCompleteTask
              ? () => {
                  setShowDependencyWarning(false);
                  statusMutation.mutate({ taskId: task.id, status: TaskStatus.COMPLETED });
                }
              : undefined
          }
        />
      )}

      <ConfirmationModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        data={
          confirmAction
            ? {
                title: confirmAction.title,
                message: confirmAction.message,
                confirmText: confirmAction.confirmText,
                type: confirmAction.type,
                reasonInput: confirmAction.reasonInput,
                onConfirm: confirmAction.onConfirm,
              }
            : undefined
        }
      />
    </>
  );
};

export default TaskDetailModal;
